import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Button, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WaitlistForm from '../components/WaitlistForm';
import waitlistService from '../firebase/waitlistService';
import { Link } from 'react-router-dom';

const LandingPage = () => {
  const [betaStats, setBetaStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysRemaining, setDaysRemaining] = useState(0);

  useEffect(() => {
    // Initialize beta stats and calculate days remaining
    const initStats = async () => {
      try {
        const stats = await waitlistService.getBetaStats();
        setBetaStats(stats);
        
        if (stats && stats.registrationCloses) {
          const closeDate = new Date(stats.registrationCloses.seconds * 1000);
          const today = new Date();
          const diffTime = Math.abs(closeDate - today);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setDaysRemaining(diffDays);
        }
      } catch (error) {
        console.error('Error initializing beta stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    initStats();
  }, []);

  const scrollToWaitlist = () => {
    const element = document.getElementById('waitlist-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 3, md: 6 }, 
          mb: 6, 
          borderRadius: 2,
          backgroundImage: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Typography variant="h2" component="h1" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '3.5rem' } }}>
            FixMatePro: Your Business WhatsApp, Working 24/7
          </Typography>
          
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Be One of the First 100 Beta Testers
          </Typography>
          
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Never Miss a Job Again - Grow Your Business With 24/7 Automation
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Your customers are already messaging you on WhatsApp. Now you can be there for them 24/7 without being chained to your phone.
          </Typography>
          
          <Button 
            variant="contained" 
            color="secondary" 
            size="large"
            onClick={scrollToWaitlist}
            sx={{ 
              py: 1.5, 
              px: 4, 
              fontSize: '1.1rem',
              fontWeight: 'bold',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Join Waitlist
          </Button>
        </Box>
      </Paper>
      
      {/* How It Works */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" textAlign="center">
          How It Works
        </Typography>
        
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center', borderTop: '4px solid #1976d2' }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                You Focus
              </Typography>
              <Typography variant="body1">
                on your work and serving your customers
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center', borderTop: '4px solid #1976d2' }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                We Handle
              </Typography>
              <Typography variant="body1">
                incoming messages and customer inquiries
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center', borderTop: '4px solid #1976d2' }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                You Choose
              </Typography>
              <Typography variant="body1">
                how much help you need, from basic to full automation
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      
      {/* Feature Highlights */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 4, height: '100%', borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              WhatsApp Integration Made Simple
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>📱 Dedicated Business Number</strong><br />
              We set up a professional WhatsApp business number for your company.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>🤖 Intelligent Automation</strong><br />
              Our AI responds to common questions and collects customer information.
            </Typography>
            <Typography variant="body1">
              <strong>📊 Complete Control</strong><br />
              You decide when to automate and when to take over the conversation.
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 4, height: '100%', borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Perfect For
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Plumbers - Book jobs while you're on a service call" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Electricians - Send quotes without interrupting your work" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="HVAC Technicians - Schedule appointments automatically" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Handymen - Never miss a customer message again" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Any Service Professional using WhatsApp for business" />
              </ListItem>
            </List>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 4, height: '100%', borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Beta Tester Benefits
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Free access during beta (3 months)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Priority support" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Shape the future of the product" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Special pricing after beta" />
              </ListItem>
            </List>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Limited Spots Available
              </Typography>
              <Typography variant="body2">
                Only 100 spots open for our beta program.
              </Typography>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={scrollToWaitlist}
                sx={{ mt: 2 }}
              >
                Join Waitlist
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Beta Timeline */}
      <Paper sx={{ p: 4, mb: 6, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center">
          Beta Timeline
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              mr: 2 
            }} />
            <Typography variant="body1">
              <strong>June 15, 2025</strong>: Beta registration closes
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              mr: 2 
            }} />
            <Typography variant="body1">
              <strong>June 20, 2025</strong>: First 50 beta users onboarded
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              mr: 2 
            }} />
            <Typography variant="body1">
              <strong>June 30, 2025</strong>: Remaining beta users onboarded
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              mr: 2 
            }} />
            <Typography variant="body1">
              <strong>July-September 2025</strong>: Beta testing period
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              mr: 2 
            }} />
            <Typography variant="body1">
              <strong>October 1, 2025</strong>: Official launch with special pricing for beta testers
            </Typography>
          </Box>
        </Box>
      </Paper>
      
  
      
      {/* FAQ Section */}
      <Paper sx={{ p: 4, mb: 6, borderRadius: 2 }}>
        <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" textAlign="center">
          Frequently Asked Questions
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Do I need any special equipment?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1">
                Just your phone and WhatsApp. We'll handle the rest. We set up a dedicated business number for you.
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">How much does it cost?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1">
                Free during beta (3 months). Special pricing for our first 100 testers after launch.
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Can I change my level of automation?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1">
                Yes! Adjust your settings anytime to fit your needs. You're in complete control.
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Will this work with my existing business process?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1">
                Absolutely! FixMatePro integrates seamlessly with how you already work. No need to change your business processes.
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Is my data secure?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1">
                100%. We use enterprise-grade security and encryption for all communications.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button 
            variant="contained" 
            color="primary"
            size="large"
            onClick={scrollToWaitlist}
            sx={{ py: 1.5, px: 4 }}
          >
            Join the Waitlist Now
          </Button>
        </Box>
      </Paper>
      
      {/* Waitlist Form */}
      <Box id="waitlist-form" sx={{ scrollMarginTop: '80px' }}>
        <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" textAlign="center">
          Join the Beta Program
        </Typography>
        
        <WaitlistForm />
        
        {/* Countdown Timer */}
        {!loading && betaStats && (
          <Paper sx={{ p: 3, mt: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Limited to the first 100 service professionals
            </Typography>
            <Typography variant="body1" color="error" fontWeight="bold">
              Only {betaStats.spotsRemaining} spots remaining! Beta registration closes in {daysRemaining} days.
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default LandingPage;
